import map from 'lodash/map'
import isEmpty from 'lodash/isEmpty'
import trim from 'lodash/trim'
import isString from 'lodash/isString'
import isFunction from 'lodash/isFunction'
import isArray from 'lodash/isArray'
import isObject from 'lodash/isObject'
import isNil from 'lodash/isNil'
import {Defaults, MEGA_BYTES} from './Constants';


export const capitalize = (s) => {
    return s && s[0].toUpperCase() + s.slice(1);
};

export const toLabel = (str) => {
    return capitalize(str.replace(/[-_]/, ' ').replace(/([A-Z]+)/, ' $1').replace(/([0-9]+)/, ' $1').replace(/ {2,}/, ' ').trim());
};

const PASSWORD_MIN_LENGTH = 8;

export const validatePassword = (password, confirmPassword) => {
    if (!isNil(confirmPassword) && password !== confirmPassword) {
        return 'Passwords don\'t match';
    }
    if (password.length < PASSWORD_MIN_LENGTH) {
        return 'Password must be at least ' + PASSWORD_MIN_LENGTH + ' characters long';
    }
    if (!password.match(/[a-zA-Z]/)) {
        return 'Password must contain some letters';
    } else if (!password.match(/[a-z]/)) {
        return 'Password must contain some lower case letters';
    } else if (!password.match(/[0-9]/)) {
        return 'Password must contain some numbers';
    }
    return null;
};


/**
 * This function is meant to be used with redux-form, by passing validation(...) to the reduxForm connector as the 'validate' property
 *
 * @param {object} validationConfig is a map with the following shape
 * property: {
 *     label: String, // the label for the property that will be shown in the error message. If not given the property itself will be used
 *     trim: Boolean, // if true, the value will be trimmed before validtion is done
 *     required: Boolean, // if true, the value will be checked not to be empty
 *     password: Boolean, // if true, the value will be checked for password straight using the validatePassword function above
 *     minLength: Integer, // the minimum length a string value should have
 *     maxLength: Integer, // the maximum length a string value should have
 *     regex: Regex, // the string value should match the given regular expression
 *     customValidator: function, // the function will be called with the value as a single argument, it should return an error message if the validation fails, or null otherwise
 *     errorMessage: String, // if given this error message will be used, instead of composing an error message internally
 *  }
 *  @param {function} extraValidation a function to perform extra validation that may need the information about more than one property value
 *  (as opposed to validationConfig above, which validates individual properties)
 *
 *  @return {object} error descriptions per property
 */
export const validation = (validationConfig, extraValidation = null) => (values) => {
    const errors = {};
    map(validationConfig, (config, prop) => {
        if (!isArray(config)) {
            // eslint-disable-next-line no-param-reassign
            config = [config];
        }
        config.forEach((cfg) => {
            let value = values[prop];
            const propLbl = cfg.label || toLabel(prop);
            const errorMsg = cfg.errorMessage;
            if (cfg.trim) {
                value = trim(value);
            }
            if (cfg.required) {
                if (!value || (isObject(value) && isEmpty(value))) {
                    errors[prop] = propLbl + ' ' + (errorMsg || 'cannot be empty');
                }
            }
            if (isString(value)) {
                if (cfg.minLength > 0) {
                    if (isEmpty(value) || value.length < cfg.minLength) {
                        errors[prop] = propLbl + ' ' + (errorMsg || 'has length ' + value.length + ', is shorter than the minimum required length ' + cfg.minLength);
                    }
                }
                if (cfg.maxLength > 0) {
                    if (!isEmpty(value) && value.length > cfg.maxLength) {
                        errors[prop] = propLbl + ' ' + (errorMsg || 'has length ' + value.length + ', exceeds the maximum allowed length ' + cfg.maxLength);
                    }
                }
                if (cfg.regex) {
                    if (!isEmpty(value) && !value.match(cfg.regex)) {
                        errors[prop] = propLbl + ' ' + (errorMsg || 'does not match the pattern ' + cfg.regex);
                    }
                }
                if (cfg.password) {
                    const confirmPassword = cfg.confirm ? values[cfg.confirm] : null;
                    const err = validatePassword(value, confirmPassword);
                    if (err) {
                        errors[prop] = err;
                    }
                }
            }
            if (isFunction(cfg.customValidator)) {
                const err = cfg.customValidator(value);
                if (err) {
                    errors[prop] = err;
                }
            }
        });
    });
    const extraErrors = extraValidation ? extraValidation(values) : {};
    return {...errors, ...extraErrors};
};

export const validateFile = (rawFile) => {
    if (rawFile.size > Defaults.UPLOAD_MAX_MB * MEGA_BYTES) {
        return 'Sorry, We currently don\'t support images over ' + Defaults.UPLOAD_MAX_MB + 'MB.';
    }
    if (rawFile.size < Defaults.UPLOAD_MIN_MB * MEGA_BYTES) {
        return 'Sorry, we don\'t support images under ' + Defaults.UPLOAD_MIN_MB + 'MB because the popping is indiscernible.';
    }
    return null;
};



// WEBPACK FOOTER //
// ./src/validation.js